/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { Suspense, useMemo } from 'react';
import { isProduction } from '@/shared/utils/environment';

/**
 * Vite replaces `import.meta.env.FF_DEV_COCKPIT` with the env value at compile
 * time. This is read at render time (not frozen at module load) so the module is
 * immune to import-order and env-timing effects under a shared worker (isolate:false).
 */
const isCockpitEnabled = (): boolean => import.meta.env.FF_DEV_COCKPIT === 'true';

const LazyDevCockpit = React.lazy(() => import('./components/DevCockpitRoot'));

if (isCockpitEnabled() && !isProduction) {
  import('./services/env-overrides').then((m) => m.applyEnvOverrides());
}

interface DevCockpitProviderProps {
  /** Called with a stable toggle function once the cockpit mounts. */
  onToggleReady?: (toggle: () => void) => void;
}

export const DevCockpitProvider: React.FC<DevCockpitProviderProps> = ({ onToggleReady }) => {
  const enabled = useMemo(() => isCockpitEnabled() && !isProduction, []);
  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <LazyDevCockpit onToggleReady={onToggleReady} />
    </Suspense>
  );
};

export default DevCockpitProvider;
